import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SkyplusLicenseService, LicenseStatus } from '../shared/license/license.service';
import { WfPermissionService } from '../wf-admin/services/wf-permission.service';

/**
 * License page (always visible): shows who the suite is licensed to, edition, modules,
 * seats (used/total or unlimited), perpetual/limited + expiry, and the bound server
 * (hostname/MAC). SKYPLUS_ADMIN users also get a "Load license" box to paste and activate
 * a new key at runtime (no redeploy) — the backend verifies + persists it.
 */
@Component({
  selector: 'skyplus-license',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="lic-wrap">
    <h1 class="lic-title">License</h1>

    <div *ngIf="loading" class="lic-muted">Loading license status…</div>
    <div *ngIf="error" class="lic-err">{{ error }}</div>

    <div *ngIf="status as s" class="lic-grid">
      <div class="lic-card">
        <div class="lic-row"><span>Licensed to</span><b>{{ s.client || 'Unknown' }}</b></div>
        <div class="lic-row"><span>Edition</span><b>{{ s.edition || '—' }}</b></div>
        <div class="lic-row"><span>Status</span>
          <b><span class="pill" [class.ok]="s.valid" [class.bad]="!s.valid">{{ s.valid ? 'Active' : 'Invalid' }}</span></b>
        </div>
        <div class="lic-row"><span>Term</span>
          <b>{{ s.perpetual ? 'Perpetual (no expiry)' : (s.expiryDate ? ('Expires ' + s.expiryDate + ' (' + s.daysUntilExpiry + ' days)') : 'Limited') }}</b>
        </div>
        <div class="lic-row"><span>Seats</span>
          <b>{{ s.seatsUnlimited ? 'Unlimited' : (s.seatsInUse + ' / ' + s.seatsTotal + ' concurrent') }}</b>
        </div>
      </div>

      <div class="lic-card">
        <div class="lic-row"><span>Modules</span>
          <b><span class="chip" *ngFor="let m of s.modules">{{ m }}</span><span *ngIf="!s.modules?.length">—</span></b>
        </div>
        <div class="lic-row"><span>Bound to server</span>
          <b>{{ s.binding === 'none' ? 'Not bound' : (s.serverHostname + ' / ' + s.serverMac) }}</b>
        </div>
        <div class="lic-row"><span>Server match</span>
          <b><span class="pill" [class.ok]="s.fingerprintOk" [class.bad]="!s.fingerprintOk">{{ s.fingerprintOk ? 'OK' : 'Mismatch' }}</span></b>
        </div>
        <div *ngIf="s.fingerprintWarning" class="lic-warn">⚠ This server does not match the licensed server. Contact SkyPlus.</div>
      </div>
    </div>

    <div *ngIf="isAdmin" class="lic-card lic-install">
      <h2>Load a new license</h2>
      <p class="lic-muted">Paste a license key issued by SkyPlus to activate additional modules. It is verified and stored on the server (survives restarts).</p>
      <textarea [(ngModel)]="token" rows="4" placeholder="header.payload.signature" [disabled]="installing"></textarea>
      <div class="lic-actions">
        <button (click)="install()" [disabled]="installing || !token.trim()">{{ installing ? 'Activating…' : 'Activate license' }}</button>
      </div>
      <div *ngIf="installMsg" class="lic-msg" [class.ok]="installOk" [class.bad]="!installOk">{{ installMsg }}</div>
    </div>
  </div>
  `,
  styles: [`
    .lic-wrap { padding: 24px; max-width: 920px; }
    .lic-title { font-size: 22px; font-weight: 600; margin: 0 0 16px; }
    .lic-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    @media (max-width: 760px) { .lic-grid { grid-template-columns: 1fr; } }
    .lic-card { border: 1px solid #e3e7ee; border-radius: 8px; padding: 16px 18px; background: #fff; }
    .lic-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f1f3f7; gap: 16px; }
    .lic-row:last-child { border-bottom: 0; }
    .lic-row > span { color: #6b7280; font-size: 13px; }
    .lic-row > b { text-align: right; font-weight: 600; }
    .pill { padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
    .pill.ok { background: #e6f4ea; color: #1e7e34; }
    .pill.bad { background: #fdecea; color: #c0392b; }
    .chip { display: inline-block; background: #eef2f8; color: #244062; border-radius: 6px; padding: 2px 8px; margin: 0 4px 4px 0; font-size: 12px; }
    .lic-muted { color: #6b7280; font-size: 13px; }
    .lic-err, .lic-warn { color: #c0392b; margin-top: 8px; font-size: 13px; }
    .lic-install { margin-top: 16px; }
    .lic-install h2 { font-size: 16px; margin: 0 0 6px; }
    .lic-install textarea { width: 100%; box-sizing: border-box; font-family: monospace; font-size: 12px; padding: 10px; border: 1px solid #cdd5e0; border-radius: 6px; margin-top: 8px; }
    .lic-actions { margin-top: 10px; }
    .lic-actions button { background: #244062; color: #fff; border: 0; border-radius: 6px; padding: 8px 16px; font-weight: 600; cursor: pointer; }
    .lic-actions button:disabled { opacity: .5; cursor: default; }
    .lic-msg { margin-top: 10px; font-size: 13px; }
    .lic-msg.ok { color: #1e7e34; }
    .lic-msg.bad { color: #c0392b; }
  `]
})
export class SkyplusLicenseComponent implements OnInit {
  private svc = inject(SkyplusLicenseService);
  private perms = inject(WfPermissionService);

  status: LicenseStatus | null = null;
  loading = true;
  error = '';
  isAdmin = false;

  token = '';
  installing = false;
  installMsg = '';
  installOk = false;

  ngOnInit(): void {
    this.isAdmin = this.perms.hasAnyRole(['SKYPLUS_ADMIN', 'ADMIN']);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.svc.getStatus().subscribe({
      next: (s) => { this.status = s; this.loading = false; },
      error: () => { this.error = 'Could not load license status.'; this.loading = false; }
    });
  }

  install(): void {
    const t = this.token.trim();
    if (!t) { return; }
    this.installing = true;
    this.installMsg = '';
    this.svc.install(t).subscribe({
      next: (r) => {
        this.installing = false;
        this.installOk = !!r.success;
        this.installMsg = r.message || (r.success ? 'License installed.' : 'Install failed.');
        if (r.success) { this.token = ''; this.load(); }
      },
      error: (e) => {
        this.installing = false;
        this.installOk = false;
        this.installMsg = e?.error?.message || 'Install failed.';
      }
    });
  }
}
